import React, { useState, useEffect } from 'react';
import { Plane, RefreshCw, AlertCircle, CheckCircle, AlertTriangle, Calendar, Infinity } from 'lucide-react';
import { getAuthHeaders } from '../../utils/authUtils';

// Quota Widget Component
const QuotaModal = ({ show, onHide, userId }) => {
  const [quotaData, setQuotaData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchQuotaData = async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log(getAuthHeaders());
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/quota/user/${userId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) throw new Error('Failed to fetch quota data');
      
      const data = await response.json();
      setQuotaData(data);
    } catch (err) {
      setError('Failed to load quota information');
      console.error('Quota fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchQuotaData();
    }
  }, [show, userId]);

  // Helper function to get status for a quota
  const getQuotaStatus = (consumedRows, totalQuota) => {
    const usagePercentage = (consumedRows / totalQuota) * 100;
    
    if (usagePercentage >= 90) {
      return { color: 'danger', icon: AlertCircle, text: 'Critical' };
    } else if (usagePercentage >= 75) {
      return { color: 'warning', icon: AlertTriangle, text: 'Warning' };
    }
    return { color: 'success', icon: CheckCircle, text: 'Healthy' };
  };

  // Separate quotas by type
  const expiringQuota = quotaData?.quotas?.find(q => !q.neverExpire);
  const permanentQuota = quotaData?.quotas?.find(q => q.neverExpire);

  // Calculate overall status based on remaining quota
  const totalRemaining = quotaData?.remainingQuotaSum || 0;
  const totalQuotaSum = quotaData?.totalQuotaSum || 1;
  const overallUsagePercentage = ((totalQuotaSum - totalRemaining) / totalQuotaSum) * 100;
  
  let overallStatus = getQuotaStatus(totalQuotaSum - totalRemaining, totalQuotaSum);

  return (
    <div className={`modal fade ${show ? 'show d-block' : ''}`} tabIndex="-1" style={{ backgroundColor: show ? 'rgba(0,0,0,0.5)' : 'transparent' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title d-flex align-items-center">
              <Plane className="me-2 text-primary" size={20} />
              Row Quota Status
            </h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onHide}
              aria-label="Close"
            ></button>
          </div>
          
          <div className="modal-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="mt-2 text-muted">Loading quota information...</div>
              </div>
            ) : error ? (
              <div className="text-center py-4">
                <AlertCircle className="text-warning mb-3" size={48} />
                <div className="text-warning mb-3">{error}</div>
                <button 
                  className="btn btn-outline-warning"
                  onClick={fetchQuotaData}
                >
                  <RefreshCw size={16} className="me-2" />
                  Try Again
                </button>
              </div>
            ) : quotaData ? (
              <>
                {/* Overall Status Badge */}
                <div className="text-center mb-4">
                  <div className={`badge bg-${overallStatus.color} fs-6 px-3 py-2 d-inline-flex align-items-center`}>
                    <overallStatus.icon size={16} className="me-2" />
                    Overall Status: {overallStatus.text}
                  </div>
                </div>

                {/* Combined Summary */}
                <div className="row text-center mb-4">
                  <div className="col-4">
                    <div className="border rounded p-3 bg-light">
                      <div className="h4 mb-1 text-primary">{quotaData.consumedRowsSum?.toLocaleString()}</div>
                      <div className="text-muted small">Total Consumed</div>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="border rounded p-3 bg-light">
                      <div className="h4 mb-1 text-success">{quotaData.remainingQuotaSum?.toLocaleString()}</div>
                      <div className="text-muted small">Total Remaining</div>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="border rounded p-3 bg-light">
                      <div className="h4 mb-1 text-secondary">{quotaData.totalQuotaSum?.toLocaleString()}</div>
                      <div className="text-muted small">Total Quota</div>
                    </div>
                  </div>
                </div>

                <hr className="my-4" />

                {/* Individual Quota Cards */}
                <div className="row g-3">
                  {/* Expiring Quota Card */}
                  {expiringQuota && (
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-header bg-info text-white d-flex align-items-center">
                          <Calendar size={18} className="me-2" />
                          <strong>Monthly Quota</strong>
                        </div>
                        <div className="card-body">
                          {(() => {
                            const usagePercentage = (expiringQuota.consumedRows / expiringQuota.totalQuota) * 100;
                            const status = getQuotaStatus(expiringQuota.consumedRows, expiringQuota.totalQuota);
                            const StatusIcon = status.icon;
                            
                            return (
                              <>
                                <div className="mb-3">
                                  <div className="d-flex justify-content-between mb-2">
                                    <span className="small fw-medium">Usage</span>
                                    <span className="small text-muted">{Math.round(usagePercentage)}%</span>
                                  </div>
                                  <div className="progress" style={{ height: '8px' }}>
                                    <div 
                                      className={`progress-bar bg-${status.color}`}
                                      style={{ width: `${usagePercentage}%` }}
                                    />
                                  </div>
                                </div>

                                <div className="row text-center g-2">
                                  <div className="col-4">
                                    <div className="small text-muted">Consumed</div>
                                    <div className="fw-bold text-primary">{expiringQuota.consumedRows.toLocaleString()}</div>
                                  </div>
                                  <div className="col-4">
                                    <div className="small text-muted">Remaining</div>
                                    <div className="fw-bold text-success">{expiringQuota.remainingQuota.toLocaleString()}</div>
                                  </div>
                                  <div className="col-4">
                                    <div className="small text-muted">Total</div>
                                    <div className="fw-bold text-secondary">{expiringQuota.totalQuota.toLocaleString()}</div>
                                  </div>
                                </div>

                                <div className="mt-3">
                                  <span className={`badge bg-${status.color} w-100`}>
                                    <StatusIcon size={14} className="me-1" />
                                    {status.text}
                                  </span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Permanent Quota Card */}
                  {permanentQuota && (
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-header bg-success text-white d-flex align-items-center">
                          <Infinity size={18} className="me-2" />
                          <strong>One Time Paid</strong>
                        </div>
                        <div className="card-body">
                          {(() => {
                            const usagePercentage = (permanentQuota.consumedRows / permanentQuota.totalQuota) * 100;
                            const status = getQuotaStatus(permanentQuota.consumedRows, permanentQuota.totalQuota);
                            const StatusIcon = status.icon;
                            
                            return (
                              <>
                                <div className="mb-3">
                                  <div className="d-flex justify-content-between mb-2">
                                    <span className="small fw-medium">Usage</span>
                                    <span className="small text-muted">{Math.round(usagePercentage)}%</span>
                                  </div>
                                  <div className="progress" style={{ height: '8px' }}>
                                    <div 
                                      className={`progress-bar bg-${status.color}`}
                                      style={{ width: `${usagePercentage}%` }}
                                    />
                                  </div>
                                </div>

                                <div className="row text-center g-2">
                                  <div className="col-4">
                                    <div className="small text-muted">Consumed</div>
                                    <div className="fw-bold text-primary">{permanentQuota.consumedRows.toLocaleString()}</div>
                                  </div>
                                  <div className="col-4">
                                    <div className="small text-muted">Remaining</div>
                                    <div className="fw-bold text-success">{permanentQuota.remainingQuota.toLocaleString()}</div>
                                  </div>
                                  <div className="col-4">
                                    <div className="small text-muted">Total</div>
                                    <div className="fw-bold text-secondary">{permanentQuota.totalQuota.toLocaleString()}</div>
                                  </div>
                                </div>

                                <div className="mt-3">
                                  <span className={`badge bg-${status.color} w-100`}>
                                    <StatusIcon size={14} className="me-1" />
                                    {status.text}
                                  </span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Alert Messages */}
                <div className="mt-4">
                  {overallUsagePercentage >= 90 && (
                    <div className="alert alert-danger">
                      <AlertCircle size={16} className="me-2" />
                      <strong>Critical:</strong> Your overall quota is nearly exhausted. Consider upgrading your plan or reducing usage.
                    </div>
                  )}
                  {overallUsagePercentage >= 75 && overallUsagePercentage < 90 && (
                    <div className="alert alert-warning">
                      <AlertTriangle size={16} className="me-2" />
                      <strong>Warning:</strong> You're approaching your quota limit. Monitor your usage carefully.
                    </div>
                  )}
                  {overallUsagePercentage < 75 && (
                    <div className="alert alert-success">
                      <CheckCircle size={16} className="me-2" />
                      Your quota usage is healthy. You have plenty of remaining quota.
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
          
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-outline-secondary"
              onClick={fetchQuotaData}
              disabled={loading}
            >
              <RefreshCw size={16} className="me-2" />
              Refresh
            </button>
            <button 
              type="button" 
              className="btn btn-primary"
              onClick={onHide}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotaModal;